import { z } from 'zod'
import {
  expandSeries,
  parseLocal,
  recurringSeriesSchema,
  zonedWallTimeToUtc,
  type RecurringSeries,
} from './recurrence.ts'
import { TAG_COLORS } from './tags.ts'

/**
 * Events, and the rule for keeping their instances in step with the series.
 *
 * `reconcileInstances` is the interesting part and the reason this is a pure
 * function rather than a loop inside the service: it decides what a series
 * change does to dates somebody has already edited, booked a room for, or
 * cancelled — and that decision is worth being able to read, argue with and
 * test on its own.
 */

export const EVENT_STATUSES = [
  'draft',
  /**
   * Sent for approval and not yet answered. Between draft and published rather
   * than beside them: everything that already asks "is this visible?" asks it
   * of `status`, and a parallel `approval_state` column would be a second
   * source of truth with one of them eventually wrong. See `approvals.ts`.
   */
  'pending_approval',
  'published',
  'cancelled',
] as const
export type EventStatus = (typeof EVENT_STATUSES)[number]

/**
 * The statuses a client may *set* directly.
 *
 * `pending_approval` is not one of them. It is reached by submitting and left
 * by being answered, and letting a PATCH write it would be a way to jump the
 * queue — or, worse, to sit in it with no request attached for anyone to find.
 */
export const SETTABLE_EVENT_STATUSES = ['draft', 'published', 'cancelled'] as const

export const EVENT_VISIBILITIES = ['public', 'members', 'staff'] as const
export type EventVisibility = (typeof EVENT_VISIBILITIES)[number]

export const INSTANCE_STATUSES = ['scheduled', 'cancelled'] as const
export type InstanceStatus = (typeof INSTANCE_STATUSES)[number]

export interface StoredInstance {
  id: string
  occurrenceDate: string
  startsAt: Date
  endsAt: Date
  status: InstanceStatus
  /** Somebody edited, moved or cancelled this one on purpose. */
  isOverridden: boolean
}

export interface PlannedInstance {
  occurrenceDate: string
  startsAt: Date
  endsAt: Date
}

export interface Reconciliation {
  /** Dates the rule produces that have no row yet. */
  create: PlannedInstance[]
  /** Rows whose times the rule has moved, and which nobody has edited. */
  update: { id: string; startsAt: Date; endsAt: Date }[]
  /** Rows the rule no longer produces, and which nobody has edited. */
  remove: string[]
  /**
   * Rows the rule no longer produces but which somebody *has* edited, or which
   * something else points at. They stay, and the caller is told: an event whose
   * series moved off a date that already has a room booked and forty people
   * registered has a problem a person needs to see, not a row to delete.
   */
  orphaned: StoredInstance[]
}

export interface ReconcileOptions {
  /** How far ahead to materialise. Instances beyond this are not our business yet. */
  horizon: Date
  /** Instances before this are history and are never touched. */
  from?: Date
  /** Hard cap, so an open-ended daily rule cannot write forever. */
  limit?: number
}

/**
 * Works out what a series change means for the rows already on disk.
 *
 * Three rules, in order of how much they matter:
 *
 * 1. An **overridden** instance is never moved and never deleted. That is the
 *    acceptance criterion: edit one week of a recurring event, then change the
 *    series, and the edit is still there afterwards.
 * 2. History is never rewritten. Instances that have already happened stay as
 *    they were, whatever the rule now says — the past is a record, not a
 *    projection.
 * 3. Everything else follows the rule.
 */
export function reconcileInstances(
  schedule: RecurringSeries | null,
  stored: readonly StoredInstance[],
  options: ReconcileOptions,
): Reconciliation {
  const from = options.from ?? new Date()
  const planned = schedule
    ? expandSeries(schedule, { from, to: options.horizon, limit: options.limit ?? 400 })
    : []

  const byDate = new Map<string, PlannedInstance>()
  for (const occurrence of planned) {
    byDate.set(occurrence.localStart.slice(0, 10), {
      occurrenceDate: occurrence.localStart.slice(0, 10),
      startsAt: occurrence.startsAt,
      endsAt: occurrence.endsAt,
    })
  }

  const existingByDate = new Map(stored.map((instance) => [instance.occurrenceDate, instance]))

  const result: Reconciliation = { create: [], update: [], remove: [], orphaned: [] }

  for (const [date, plan] of byDate) {
    const existing = existingByDate.get(date)
    if (!existing) {
      result.create.push(plan)
      continue
    }
    // Rule 1: an edited instance keeps its own times.
    if (existing.isOverridden) continue
    if (
      existing.startsAt.getTime() !== plan.startsAt.getTime() ||
      existing.endsAt.getTime() !== plan.endsAt.getTime()
    ) {
      result.update.push({ id: existing.id, startsAt: plan.startsAt, endsAt: plan.endsAt })
    }
  }

  for (const instance of stored) {
    if (byDate.has(instance.occurrenceDate)) continue
    // Rule 2: the past is a record.
    if (instance.startsAt < from) continue
    // Beyond the horizon is simply not planned yet, so it is not orphaned.
    if (instance.startsAt > options.horizon) continue

    if (instance.isOverridden) result.orphaned.push(instance)
    else result.remove.push(instance.id)
  }

  return result
}

/** The window a calendar asks for, clamped to something a query can serve. */
export function calendarWindow(
  from?: string,
  to?: string,
  maxDays = 400,
): { from: Date; to: Date } {
  const start = from ? new Date(from) : new Date()
  const requested = to ? new Date(to) : new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000)
  const ceiling = new Date(start.getTime() + maxDays * 24 * 60 * 60 * 1000)
  return { from: start, to: requested > ceiling ? ceiling : requested }
}

// ---- schemas ---------------------------------------------------------------

/**
 * Long enough for a camp's whole information sheet — what churches paste into
 * Planning Center — and short enough that nobody pastes a book by mistake.
 */
export const EVENT_DESCRIPTION_MAX = 20_000

const blankToNull = (v: string | null | undefined) => (v === '' || v === undefined ? null : v)

/** Optional link fields, shared by create and update. */
const linkUrl = z
  .string()
  .trim()
  .max(2000)
  .refine((v) => v === '' || /^https?:\/\//i.test(v), 'A link starts with http:// or https://')
const linkLabel = z.string().trim().max(40)

export const createEventSchema = z
  .object({
    title: z.string().trim().min(1, 'Give the event a name').max(160),
    description: z.string().trim().max(EVENT_DESCRIPTION_MAX).nullish().transform(blankToNull),
    location: z
      .string()
      .trim()
      .max(200)
      .nullish()
      .transform((v) => (v === '' || v === undefined ? null : v)),
    category: z
      .string()
      .trim()
      .max(60)
      .nullish()
      .transform((v) => (v === '' || v === undefined ? null : v)),
    campusId: z.uuid().nullish(),
    visibility: z.enum(EVENT_VISIBILITIES).default('members'),
    status: z.enum(SETTABLE_EVENT_STATUSES).default('draft'),
    /** A recurring event. Mutually exclusive with a one-off's start/end. */
    schedule: recurringSeriesSchema.nullish(),
    /** A one-off. Mutually exclusive with a schedule. */
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    /** Days, not times (`allDayWindow` makes the window). */
    allDay: z.boolean().default(false),
    featured: z.boolean().default(false),
    linkUrl: linkUrl.nullish().transform(blankToNull),
    linkLabel: linkLabel.nullish().transform(blankToNull),
    /** The event's tags; replaces the whole set on update. */
    tagIds: z.array(z.uuid()).max(50).default([]),
  })
  .refine((event) => event.schedule != null || event.startsAt !== undefined, {
    message: 'An event needs either a date or a repeating schedule',
    path: ['startsAt'],
  })
  .refine((event) => event.schedule == null || event.startsAt === undefined, {
    message: 'Give a schedule or a single date, not both',
    path: ['schedule'],
  })
  .refine(
    (event) =>
      event.startsAt === undefined ||
      (event.endsAt !== undefined && new Date(event.endsAt) > new Date(event.startsAt)),
    { message: 'The end has to come after the start', path: ['endsAt'] },
  )
export type CreateEventInput = z.infer<typeof createEventSchema>
export type CreateEventFormInput = z.input<typeof createEventSchema>

/** Everything on an event can change; what it *is* cannot become nothing. */
export const updateEventSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  description: z.string().trim().max(EVENT_DESCRIPTION_MAX).nullish(),
  location: z.string().trim().max(200).nullish(),
  category: z.string().trim().max(60).nullish(),
  campusId: z.uuid().nullish(),
  visibility: z.enum(EVENT_VISIBILITIES).optional(),
  status: z.enum(SETTABLE_EVENT_STATUSES).optional(),
  schedule: recurringSeriesSchema.nullish(),
  allDay: z.boolean().optional(),
  featured: z.boolean().optional(),
  linkUrl: linkUrl.nullish().transform((v) => (v === undefined ? undefined : blankToNull(v))),
  linkLabel: linkLabel.nullish().transform((v) => (v === undefined ? undefined : blankToNull(v))),
  tagIds: z.array(z.uuid()).max(50).optional(),
})
export type UpdateEventInput = z.infer<typeof updateEventSchema>

/**
 * Editing one date of a series.
 *
 * Any of these marks the instance overridden, which is what protects it from
 * the next series change.
 */
export const updateInstanceSchema = z.object({
  title: z.string().trim().max(160).nullish(),
  description: z.string().trim().max(EVENT_DESCRIPTION_MAX).nullish(),
  location: z.string().trim().max(200).nullish(),
  status: z.enum(INSTANCE_STATUSES).optional(),
  startsAt: z.iso.datetime().optional(),
  endsAt: z.iso.datetime().optional(),
})
export type UpdateInstanceInput = z.infer<typeof updateInstanceSchema>

// ---- all-day events ------------------------------------------------------------

const DAY = /^\d{4}-\d{2}-\d{2}$/

/** The day after a `YYYY-MM-DD`, as one. */
function nextDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString().slice(0, 10)
}

/**
 * An all-day event's window: midnight at the start of the first day to
 * midnight after the last, in the zone it is planned in. Real instants, so
 * the calendar, bookings and feeds order and overlap it like anything else;
 * the `allDay` flag is what tells a screen to say "All day" instead.
 */
export function allDayWindow(
  firstDay: string,
  lastDay: string,
  timeZone: string,
): { startsAt: Date; endsAt: Date } {
  if (!DAY.test(firstDay) || !DAY.test(lastDay)) throw new Error('Days are YYYY-MM-DD')
  const last = lastDay < firstDay ? firstDay : lastDay
  return {
    startsAt: zonedWallTimeToUtc(parseLocal(`${firstDay}T00:00`), timeZone),
    endsAt: zonedWallTimeToUtc(parseLocal(`${nextDay(last)}T00:00`), timeZone),
  }
}

/** How many days an all-day series' dates last, from a first and last day. */
export function allDayDurationMinutes(firstDay: string, lastDay: string): number {
  const days =
    Math.round(
      (Date.parse(`${lastDay}T00:00:00Z`) - Date.parse(`${firstDay}T00:00:00Z`)) / 86_400_000,
    ) + 1
  return Math.max(1, days) * 24 * 60
}

// ---- event tags ----------------------------------------------------------------

export const createEventTagGroupSchema = z.object({
  name: z.string().trim().min(1, 'Name the group').max(60),
})
export const createEventTagSchema = z.object({
  groupId: z.uuid('Choose a group'),
  name: z.string().trim().min(1, 'Name the tag').max(60),
  color: z.enum(TAG_COLORS).nullish(),
})
export const updateEventTagSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  color: z.enum(TAG_COLORS).nullish(),
})

/**
 * A calendar filter from the tags somebody picked: any tag within a group,
 * every group picked. "Youth or Kids, and Families" — how Planning Center's
 * Church Center filters, and what people mean when they pick from two lists.
 * Tags the church no longer has are dropped rather than matching nothing.
 */
export function tagFilterGroups(
  selected: readonly string[],
  tags: readonly { id: string; groupId: string }[],
): string[][] {
  const groupOf = new Map(tags.map((tag) => [tag.id, tag.groupId]))
  const byGroup = new Map<string, string[]>()
  for (const id of new Set(selected)) {
    const group = groupOf.get(id)
    if (!group) continue
    byGroup.set(group, [...(byGroup.get(group) ?? []), id])
  }
  return [...byGroup.values()]
}

/** Whether an event's tags pass a filter from `tagFilterGroups`. */
export function matchesTagFilter(eventTagIds: readonly string[], groups: readonly string[][]) {
  const carried = new Set(eventTagIds)
  return groups.every((group) => group.some((id) => carried.has(id)))
}
